import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem, Button, FormControlLabel, Typography, Checkbox } from '@mui/material';
import Context from '../../context/Context';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';
import {
  //   TextField,
  IconButton,
  InputAdornment
} from '@mui/material';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const AddEditEmployee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const employee = location.state;
  const { activeRoles, getAllRoles } = useContext(Context);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    roleId: '',
    isActive: true,
    isDeleted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');

  const getApiFieldError = (message = '') => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('email')) {
      return { email: message };
    }

    if (lowerMessage.includes('mobile') || lowerMessage.includes('phone')) {
      return { mobile: message };
    }

    return {};
  };

  useEffect(() => {
    getAllRoles();

    if (employee) {
      setForm({
        name: employee.name || '',
        email: employee.email || '',
        mobile: employee.mobile || '',
        password: '',
        roleId: employee.roleId || '',
        isActive: employee.isActive,
        isDeleted: employee.isDeleted || false,
      });
    }
  }, [employee]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setFormError('');
  };
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});
    setFormError('');

    const payload = {
      name: form.name,
      email: form.email,
      mobile: form.mobile,
      ...(form.password && { password: form.password }),
      roleId: form.roleId,
      isActive: form.isActive,
      isDeleted: form.isDeleted
    };

    try {
      let response;
      // if (employee) {
      //   response = await axios.put(`${apiUrl}/employee/updateemployee/${employee.id}`, payload);
      // } else {
      //   response = await axios.post(`${apiUrl}/employee/employee`, payload);
      // }
      if (employee) {
        response = await apiClient.put(`/employee/updateemployee/${employee.id}`, payload);
      } else {
        response = await apiClient.post('/employee/employee', payload);
      }
      if (response.data.status === true) {
        toast.success(response.data.message);
        navigate('/display-employees');
      } else {
        const message = response?.data?.message || 'Failed to save employee';
        const apiFieldError = getApiFieldError(message);

        setFieldErrors(apiFieldError);
        if (Object.keys(apiFieldError).length === 0) {
          setFormError(message);
        }
      }

    } catch (err) {
      const message = err?.response?.data?.message || 'Something went wrong. Please try again later.';
      const apiFieldError = getApiFieldError(message);

      setFieldErrors(apiFieldError);
      if (Object.keys(apiFieldError).length === 0) {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box maxWidth={500} mr="auto" m={2} sx={{ border: '1px solid lightgrey', borderRadius: '5px' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" mb={2}>
          {employee ? 'Edit Employee' : 'Add New Employee'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            name="name"
            label="Name"
            margin="normal"
            value={form.name}
            onChange={handleChange}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            required
            size='small'
          />
          <TextField
            fullWidth
            name="email"
            label="Email"
            margin="normal"
            type="email"
            value={form.email}
            onChange={handleChange}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            required
            size='small'
          />
          <TextField
            fullWidth
            name="mobile"
            label="Mobile"
            margin="normal"
            value={form.mobile}
            onChange={handleChange}
            error={!!fieldErrors.mobile}
            helperText={fieldErrors.mobile}
            required
            size='small'
          />
          <TextField
            fullWidth
            name="password"
            label="Password"
            margin="normal"
            // type="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            error={!!fieldErrors.password}
            helperText={fieldErrors.password || (employee ? 'Leave blank to keep current password' : '')}
            onChange={handleChange}
            required={!employee}
            size='small'
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            name="roleId"
            label="Role"
            margin="normal"
            value={form.roleId}
            onChange={handleChange}
            error={!!fieldErrors.roleId}
            helperText={fieldErrors.roleId}
            required
            size='small'
          >
            {activeRoles && activeRoles.length > 0 ? (
              activeRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No roles available
              </MenuItem>
            )}
          </TextField>

          <FormControlLabel
            control={
              <Checkbox name="isActive" checked={form.isActive} onChange={handleChange} />
            } label="Is Active"
          />

          <FormControlLabel
            control={
              <Checkbox name="isDeleted" checked={form.isDeleted} onChange={handleChange} />
            } label="Is Deleted"
          />

          {formError && (
            <Typography color="error" variant="body2" mt={1}>
              {formError}
            </Typography>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button variant="contained" color="primary" type="submit" disabled={submitting}>
              {employee ? 'Update' : 'Create'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/display-employees')} disabled={submitting}>
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default AddEditEmployee;
